import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, Heading } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { GraphAPI, type GraphNetwork } from '@/api/graph'
import { CreditsAPI, creditReasonLabel, formatDelta } from '@/api/credits'
import { HelpHint } from '@/components/HelpHint'
import { IdentityEditor } from '@/components/IdentityEditor'
import { SigilsStrip } from '@/components/Sigils'
import { HomeSurfaceActivity } from '@/components/ActivityHeatmap'
import {
  publicCount,
  readVisibility,
  toggleSection,
  type ProfileSection,
  type ProfileVisibility,
  type Visibility,
} from '@/lib/profile'
import './profile.css'

// The Profile — your identity + PRESENCE, in one place. It is the canonical home for the three
// things the suite already builds about "you": the identity kernel ("About you"), your earned
// sigils, and your progression (activity + streak). It is deliberately NOT Settings: Settings is
// private controls; Profile is the shareable face of you.
//
// Every section carries a public/private flag — the sovereignty membrane. Public = shown in
// Commune/Eden; private = only you. The flags live on the ego (attributes.profile_visibility), so
// they are suite-shared: the same map the future GET /profile/{handle} public-read endpoint will
// honour when Commune/Eden render this profile for others. No new backend is needed for your own
// view — identity comes from the self node of GET /graph/network, sigils from GET /sigils, and
// progression from GET /graph/history (day) — all composed client-side here.

const QK_NETWORK = ['graph-network', 'all'] as const

/** The public/private switch for one section — the membrane made legible and one-click. */
function VisibilityToggle({
  value,
  onToggle,
  busy,
}: {
  value: Visibility
  onToggle: () => void
  busy: boolean
}) {
  const isPublic = value === 'public'
  return (
    <button
      type="button"
      className={`dw-vis dw-vis--${value}`}
      aria-pressed={isPublic}
      disabled={busy}
      onClick={onToggle}
      title={
        isPublic
          ? 'Public — shown to others in Commune & Eden. Click to make it private.'
          : 'Private — only you can see this. Click to show it in Commune & Eden.'
      }
    >
      <span className="dw-vis-dot" aria-hidden="true" />
      <span className="dw-vis-label">{isPublic ? 'Shown in Commune' : 'Only you'}</span>
    </button>
  )
}

/** One Profile section: a titled card with the membrane toggle in its header. */
function Section({
  title,
  hint,
  section,
  visibility,
  onToggle,
  busy,
  aside,
  children,
}: {
  title: string
  hint: string
  section: ProfileSection
  visibility: ProfileVisibility
  onToggle: (s: ProfileSection) => void
  busy: boolean
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card>
      <div className="dw-profile-section-head">
        <Heading level={3}>
          {title} <HelpHint gloss={hint} />
        </Heading>
        <div className="dw-profile-section-controls">
          {aside}
          <VisibilityToggle value={visibility[section]} onToggle={() => onToggle(section)} busy={busy} />
        </div>
      </div>
      {children}
    </Card>
  )
}

/**
 * Credits — the ONE suite credit, in the progression corner. Balance + a compact recent
 * ledger, read from GET /credits. Credits are EARNED by using the suite (each milestone
 * sigil grants once); spending is coming. Not a membrane section — your balance is always
 * yours, so no public/private toggle here.
 */
function CreditsCard() {
  const { diwaUrl } = useUI()
  const creditsApi = useMemo(() => new CreditsAPI(diwaUrl), [diwaUrl])
  const { data, isLoading, isError } = useQuery({
    queryKey: ['credits'],
    queryFn: () => creditsApi.get(),
  })

  const recent = (data?.ledger ?? []).slice(0, 5)

  return (
    <Card>
      <div className="dw-profile-section-head">
        <Heading level={3}>
          Credits <HelpHint gloss="One suite-wide credit, earned as you use Diwata — each milestone sigil you reach grants credit once. Carried across every app. Spending them comes soon." />
        </Heading>
      </div>

      {isError ? (
        <div className="dw-rail-empty">Couldn't load your credits — reload to try again.</div>
      ) : (
        <>
          <div className="dw-credits-balance">
            <span className="dw-credits-glyph" aria-hidden="true">◈</span>
            <span className="dw-credits-amount">{isLoading ? '—' : (data?.balance ?? 0).toLocaleString()}</span>
            <span className="dw-credits-unit">credits</span>
          </div>

          {!isLoading && recent.length > 0 && (
            <ul className="dw-credits-ledger">
              {recent.map((row, i) => (
                <li className="dw-credits-row" key={`${row.reason}-${row.created_at ?? i}`}>
                  <span className={`dw-credits-delta dw-credits-delta--${row.delta >= 0 ? 'up' : 'down'}`}>
                    {formatDelta(row.delta)}
                  </span>
                  <span className="dw-credits-reason">{creditReasonLabel(row.reason)}</span>
                </li>
              ))}
            </ul>
          )}

          {!isLoading && recent.length === 0 && (
            <div className="dw-credits-empty">No credits yet — earn your first by tending your world.</div>
          )}

          <div className="dw-profile-note">
            Earned by using the suite — every milestone you reach adds credit. Spending them arrives soon.
          </div>
        </>
      )}
    </Card>
  )
}

export function Profile() {
  const { diwaUrl } = useUI()
  const qc = useQueryClient()
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])

  const { data: network, isLoading, isError } = useQuery({
    queryKey: QK_NETWORK,
    queryFn: () => graphApi.network('all'),
  })

  const ego = network?.nodes.find((n) => n.self)
  const visibility = readVisibility(ego?.attributes)
  const shown = publicCount(visibility)

  // Optimistically reflect an identity save into the cached network so the "About you" view
  // updates before the invalidated query refetches (the editor also invalidates on its own).
  function onIdentitySaved(identity: Record<string, unknown>) {
    if (!ego) return
    qc.setQueryData<GraphNetwork>(QK_NETWORK, (prev) =>
      prev
        ? {
            ...prev,
            nodes: prev.nodes.map((n) =>
              n.id === ego.id ? { ...n, attributes: { ...(n.attributes ?? {}), identity } } : n,
            ),
          }
        : prev,
    )
  }

  // Toggle one section's flag: read-modify-write the whole membrane back onto the ego, optimistic
  // in the cache first so the pill flips at once, then PATCH. On failure we roll the cache back.
  async function toggle(section: ProfileSection) {
    if (!ego) return
    const next = toggleSection(visibility, section)
    const prevNetwork = network
    qc.setQueryData<GraphNetwork>(QK_NETWORK, (prev) =>
      prev
        ? {
            ...prev,
            nodes: prev.nodes.map((n) =>
              n.id === ego.id
                ? { ...n, attributes: { ...(n.attributes ?? {}), profile_visibility: next } }
                : n,
            ),
          }
        : prev,
    )
    try {
      await graphApi.patchEntityAttributes(ego.id, { profile_visibility: next })
    } catch {
      qc.setQueryData<GraphNetwork>(QK_NETWORK, prevNetwork) // roll back on failure
    }
  }

  return (
    <div className="dw-profile">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Profile <HelpHint gloss="Your identity and presence — the shareable face of you. Each section can be public (shown in Commune & Eden) or kept only to you." />
          </div>
          <div className="dw-page-subtitle">
            {isLoading
              ? 'Reading your profile…'
              : ego
                ? `Your identity, sigils, and progression — ${shown} of 3 sections shown in Commune.`
                : 'Your identity, sigils, and progression, in one place.'}
          </div>
        </div>
      </div>

      {isError && (
        <div className="dw-error" style={{ marginBottom: 16 }}>
          Couldn't load your profile — reload to try again.
        </div>
      )}

      <div className="dw-profile-membrane">
        The <strong>sovereignty membrane</strong>: each section below is either{' '}
        <span className="dw-vis-inline dw-vis-inline--public">shown in Commune &amp; Eden</span> or{' '}
        <span className="dw-vis-inline dw-vis-inline--private">only you</span>. Nothing is shared until you say so.
      </div>

      <div className="dw-profile-sections">
        {/* ── About you — the identity kernel, now with its canonical home here ── */}
        <Section
          title="About you"
          hint="Your name, what you do, your expertise, and what you're building toward — the identity kernel your familiar reads. Editable here and in your dossier."
          section="identity"
          visibility={visibility}
          onToggle={toggle}
          busy={!ego}
        >
          {ego ? (
            <IdentityEditor
              egoId={ego.id}
              identity={ego.attributes?.identity}
              graphApi={graphApi}
              onSaved={onIdentitySaved}
              hideTitle
            />
          ) : (
            <div className="dw-rail-empty">{isLoading ? 'Reading your identity…' : 'Your center star hasn’t formed yet.'}</div>
          )}
          <div className="dw-profile-note">
            When public, your name, what you do, and your expertise are visible in Commune &amp; Eden.
            Your goals (&ldquo;building toward&rdquo;) always stay private, whatever this is set to.
          </div>
        </Section>

        {/* ── Your sigils — the earned gallery, strip form ── */}
        <Section
          title="Your sigils"
          hint="Suite-wide marks of progression you've earned from your own activity — carried across every Diwata app."
          section="sigils"
          visibility={visibility}
          onToggle={toggle}
          busy={!ego}
          aside={<Link className="dw-link" to="/sigils">see all &rarr;</Link>}
        >
          <SigilsStrip />
        </Section>

        {/* ── Your progression — activity heatmap + streak ── */}
        <Section
          title="Your progression"
          hint="Your streak and activity across the whole verse — every star, link, signal and mention you or your familiars added. Each active day feeds your streak."
          section="progression"
          visibility={visibility}
          onToggle={toggle}
          busy={!ego}
        >
          <HomeSurfaceActivity />
        </Section>

        {/* ── Credits — the one suite credit, earned from progression (no membrane toggle) ── */}
        <CreditsCard />
      </div>
    </div>
  )
}
